import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { auth } from './firebase';
import API_URL from './config';
import { APP_FEATURES } from './appFeatures';
import './Login.css';
import { FiMail, FiLock, FiUser, FiArrowRight, FiCheckCircle, FiEye, FiEyeOff } from 'react-icons/fi';

const getFriendlyError = (err) => {
  const code = err.code || '';
  const msg = err.message || '';

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'Incorrect email or password. Please try again.';
    case 'auth/user-not-found':
      return 'No account found with this email address. Did you mean to create a new account?';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try signing in instead.';
    case 'auth/weak-password':
      return 'Password is too weak. It must be at least 6 characters long.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a moment and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/requires-recent-login':
      return 'For security, please sign in again before making this change.';
    default:
      // Strip Firebase prefix from any unhandled messages
      return msg.replace(/Firebase:\s*/i, '').replace(/\(auth\/[\w-]+\)\.?/i, '').trim() || 'Something went wrong. Please try again.';
  }
};

const Login = () => {
  const location = useLocation();
  // Honour an explicit mode (e.g. the "Create account" button on the logged-out
  // home navigates here with { mode: 'register' }).
  const [view, setView] = useState(location.state?.mode === 'register' ? 'register' : 'login'); // 'login' | 'register' | 'forgot'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [resetEmail, setResetEmail] = useState('');

  const { login, signup, resetPassword, fetchUserProfile, waitForProfile } = useAuth();
  const navigate = useNavigate();
  const accountDeleted = location.state?.accountDeleted;
  const isRegistering = view === 'register';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isRegistering) {
        if (!name) {
          setError('Please enter your full name.');
          setLoading(false);
          return;
        }
        if (password !== confirmPassword) {
          setError('Passwords do not match. Please re-enter them.');
          setLoading(false);
          return;
        }
        if (!agreedToTerms) {
          setError('You must agree to the Terms & Conditions and Privacy Policy.');
          setLoading(false);
          return;
        }
        await signup(email, password, name);
        // Let the auth listener's own profile fetch land first. Run alongside
        // the two below, a slow first answer (no terms yet) could arrive LAST
        // and put the terms prompt over Home straight after sign-up.
        await waitForProfile();

        // Force token refresh so the displayName set via updateProfile
        // is included in the ID token's 'name' claim
        await auth.currentUser.getIdToken(true);

        // GET /auth/me creates the backend user record using the name
        // from the Firebase token. Then PUT to ensure the name is synced
        // (in case the token claim lagged behind).
        const profile = await fetchUserProfile(auth.currentUser);
        if (profile) {
          const token = await auth.currentUser.getIdToken();
          await fetch(`${API_URL}/auth/me`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ display_name: name, terms_accepted_at: new Date().toISOString() })
          });
          // Refresh the profile so terms_accepted_at is in context — otherwise
          // the TermsAcceptanceGate re-prompts straight after signup.
          await fetchUserProfile(auth.currentUser);
        }
      } else {
        await login(email, password);
        // This screen stays up (button busy) until the profile is in, so Home
        // opens on a known user and society rather than filling in after.
        await waitForProfile();
      }
      
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      const pendingCode = localStorage.getItem('pendingInviteCode');

      if (redirect === 'join' && pendingCode) {
        // Auto-join: call join-society directly so user skips the confirmation page
        try {
          const joinToken = await auth.currentUser.getIdToken();
          const joinRes = await fetch(`${API_URL}/auth/join-society`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${joinToken}`
            },
            body: JSON.stringify({ invite_code: pendingCode })
          });
          const joinData = await joinRes.json();
          if (joinRes.ok || joinData.error === 'You are already a member of this society') {
            localStorage.removeItem('pendingInviteCode');
            await fetchUserProfile(auth.currentUser);
            navigate('/home');
          } else {
            // Auto-join failed — fall back to the join page for manual retry
            navigate(`/join?code=${pendingCode}`);
          }
        } catch {
          navigate(`/join?code=${pendingCode}`);
        }
      } else {
        navigate('/home');
      }
    } catch (err) {
      setError(getFriendlyError(err));
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      return setError('Please enter your email address.');
    }
    setLoading(true);
    setError('');
    try {
      await resetPassword(resetEmail);
      setMessage('Check your email for password reset instructions.');
    } catch (err) {
      setError(getFriendlyError(err));
    }
    setLoading(false);
  };

  const switchView = (newView) => {
    setView(newView);
    setError('');
    setMessage('');
    setName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setResetEmail('');
    setAgreedToTerms(false);
  };

  if (view === 'forgot') {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src={`${process.env.PUBLIC_URL}/1_19_logo.png`} alt="1-19 Logo" className="login-logo" />
            <h2>Reset Password</h2>
            <p>Enter your email and we'll send you a reset link</p>
          </div>

          {accountDeleted && <div className="auth-message">Your account has been deleted. Thanks for using 1-19 Golf.</div>}
          {error && <div className="auth-error">{error}</div>}
          {message && <div className="auth-message">{message}</div>}

          {!message ? (
            <form onSubmit={handleForgotPassword} className="login-form">
              <div className="input-group">
                <FiMail className="input-icon" size={20} />
                <input
                  type="email"
                  name="email"
                  autoComplete="username"
                  autoCapitalize="none"
                  placeholder="Email Address"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <button type="submit" className="login-btn" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
                <FiArrowRight style={{ marginLeft: '8px' }} />
              </button>
            </form>
          ) : null}

          <div className="login-footer">
            <button className="text-btn" onClick={() => switchView('login')}>
              Back to Log In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src={`${process.env.PUBLIC_URL}/1_19_logo.png`} alt="1-19 Logo" className="login-logo" />
        </div>

        <div className="auth-mode-toggle">
          <button
            className={`auth-mode-tab ${!isRegistering ? 'active' : ''}`}
            onClick={() => switchView('login')}
            type="button"
          >
            Sign In
          </button>
          <button
            className={`auth-mode-tab ${isRegistering ? 'active' : ''}`}
            onClick={() => switchView('register')}
            type="button"
          >
            Create Account
          </button>
        </div>

        <p className="auth-mode-hint">
          {isRegistering
            ? 'New here? Create an account to get started.'
            : 'Already have an account? Sign in below.'}
        </p>

        {error && <div className="auth-error">{error}</div>}
        {message && <div className="auth-message">{message}</div>}

        <form onSubmit={handleSubmit} className="login-form">
          {isRegistering && (
            <div className="input-group">
              <FiUser className="input-icon" size={20} />
              <input
                type="text"
                name="name"
                autoComplete="name"
                placeholder="Full Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
          )}

          <div className="input-group">
            <FiMail className="input-icon" size={20} />
            {/* The autofill hints are what let a phone OFFER TO SAVE the login.
                Inside the app there is no address bar for Google Password
                Manager (or iOS Keychain) to go on, so the Android WebView
                passes these attributes to the system's autofill service and it
                classifies the form from them; with none, Android users were
                never asked to save. `username` rather than `email` is the
                documented hint for the account identifier even when it is an
                email address; the password hint is what separates signing in
                from choosing a new one, which is also what stops a saved
                password being offered into Create Account. */}
            <input
              type="email"
              name="email"
              id="login-email"
              autoComplete="username"
              autoCapitalize="none"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="input-group">
            <FiLock className="input-icon" size={20} />
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="login-password"
              autoComplete={isRegistering ? 'new-password' : 'current-password'}
              className="has-toggle"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
          </div>

          {isRegistering && (
            <div className="input-group">
              <FiLock className="input-icon" size={20} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirm-password"
                autoComplete="new-password"
                className="has-toggle"
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword((v) => !v)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showConfirmPassword}
              >
                {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          )}

          {isRegistering && (
            <label className="terms-checkbox">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              <span>
                I agree to the{' '}
                <a href="/legal?tab=terms" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</a>
                {' '}and{' '}
                <a href="/legal?tab=privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
              </span>
            </label>
          )}

          <button type="submit" className={`login-btn ${isRegistering ? 'register-btn' : ''}`} disabled={loading || (isRegistering && !agreedToTerms)}>
            {loading
              ? (isRegistering ? 'Creating your account…' : 'Signing in…')
              : (isRegistering ? 'Create Account' : 'Sign In')}
            <FiArrowRight style={{ marginLeft: '8px' }} />
          </button>
        </form>

        <div className="login-footer">
          {!isRegistering && (
            <button className="text-btn" onClick={() => switchView('forgot')}>
              Forgot Password?
            </button>
          )}
          <a href="/legal" className="text-btn login-legal-link">
            Terms & Privacy
          </a>
        </div>

        <ul className="login-features">
          {APP_FEATURES.map((f) => (
            <li key={f}><FiCheckCircle className="login-feature-icon" /> {f}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Login;